import type { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import { DEXIE_MIGRATION_TABLES } from '@infra/persistence/Migrations/dexie_to_sqlite';
import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { SQLITE_AUX_TABLES } from '@infra/persistence/SqliteTableName';
import { sanitizeBackupBranding } from '@infra/backup/brandingSanitize';

export interface BackupExportResult {
  data: Record<string, unknown[]>;
  counts: Record<string, number>;
}

/** Export des tables métier depuis la source active (SQLite ou Dexie). */
export async function exportBackupData(
  db: NexxusDatabase,
  tenantId: string,
): Promise<BackupExportResult> {
  if (await SqliteEntityBridge.isSqlitePrimary()) {
    return exportFromSqlite(tenantId);
  }
  return exportFromDexie(db, tenantId);
}

export interface ImportBackupOptions {
  /** Tout ou rien : une seule transaction ; en cas d'échec, la base reste inchangée. */
  atomic?: boolean;
}

export async function importBackupData(
  db: NexxusDatabase,
  rawData: Record<string, unknown[]>,
  options: ImportBackupOptions = {},
): Promise<Record<string, number>> {
  // La charte graphique du client (logo) vient d'un fichier non fiable : revérifiée avant toute écriture.
  const data = sanitizeBackupBranding(rawData);
  if (await SqliteEntityBridge.isSqlitePrimary()) {
    return options.atomic ? importToSqliteAtomic(data) : importToSqlite(data);
  }
  return options.atomic ? importToDexieAtomic(db, data) : importToDexie(db, data);
}

function collectAtomicTables(data: Record<string, unknown[]>): Record<string, unknown[]> {
  const tables: Record<string, unknown[]> = {};
  for (const table of [...DEXIE_MIGRATION_TABLES, 'module_data'] as string[]) {
    const rows = data[table];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (!row || typeof row !== 'object' || typeof (row as { id?: unknown }).id !== 'string') {
        throw new Error(`Sauvegarde invalide : ligne sans identifiant dans ${table}`);
      }
    }
    tables[table] = rows;
  }
  return tables;
}

async function importToSqliteAtomic(data: Record<string, unknown[]>): Promise<Record<string, number>> {
  const fn = window.electron?.hybridImportAtomic;
  if (!fn) throw new Error('Import atomique SQLite indisponible');
  const res = await fn({ tables: collectAtomicTables(data) as Record<string, Record<string, unknown>[]> });
  if (!res?.ok) throw new Error('Import atomique refusé');
  return res.counts;
}

/** Dexie : tables dans UNE transaction IndexedDB ; module_data ensuite avec retour arrière si échec. */
async function importToDexieAtomic(
  db: NexxusDatabase,
  data: Record<string, unknown[]>,
): Promise<Record<string, number>> {
  const tables = collectAtomicTables(data);
  const restored: Record<string, number> = {};
  const dexieTables = DEXIE_MIGRATION_TABLES.filter((t) => Array.isArray(tables[t]));
  const handles = dexieTables
    .map((t) => (db as unknown as Record<string, unknown>)[t])
    .filter(Boolean) as Array<{ bulkPut: (rows: unknown[]) => Promise<unknown> }>;
  await (db as unknown as { transaction: (...a: unknown[]) => Promise<void> }).transaction(
    'rw',
    handles,
    async () => {
      for (const t of dexieTables) {
        const table = (db as unknown as Record<string, { bulkPut: (rows: unknown[]) => Promise<unknown> }>)[t];
        if (!table) continue;
        await table.bulkPut(tables[t]!);
        restored[t] = tables[t]!.length;
      }
    },
  );
  const moduleRows = tables['module_data'] as ModuleDataRowLike[] | undefined;
  if (moduleRows) {
    const tenantIds = [...new Set(moduleRows.map((r) => r.tenant_id).filter(Boolean))] as string[];
    const before: ModuleDataRowLike[] = [];
    for (const tid of tenantIds) before.push(...((await SecureTenantStore.listForTenant(tid)) as ModuleDataRowLike[]));
    try {
      for (const row of moduleRows) {
        if (!row?.tenant_id || !row?.namespace) continue;
        await SecureTenantStore.setJson(row.tenant_id, row.namespace, row.value);
      }
    } catch (err) {
      for (const row of before) {
        if (row.tenant_id && row.namespace) {
          await SecureTenantStore.setJson(row.tenant_id, row.namespace, row.value).catch(() => undefined);
        }
      }
      throw err;
    }
    restored['module_data'] = moduleRows.length;
  }
  return restored;
}

async function exportFromSqlite(tenantId: string): Promise<BackupExportResult> {
  const data: Record<string, unknown[]> = {};
  const counts: Record<string, number> = {};

  for (const table of DEXIE_MIGRATION_TABLES) {
    const rows =
      table === 'tenants'
        ? await SqliteEntityBridge.list(table)
        : await SqliteEntityBridge.list(table, tenantId);
    const filtered =
      table === 'tenants' ? rows.filter((r) => (r as { id?: string }).id === tenantId) : rows;
    data[table] = filtered;
    counts[table] = filtered.length;
  }

  const moduleRows = await SecureTenantStore.listForTenant(tenantId);
  data['module_data'] = moduleRows;
  counts['module_data'] = moduleRows.length;

  return { data, counts };
}

async function exportFromDexie(db: NexxusDatabase, tenantId: string): Promise<BackupExportResult> {
  const data: Record<string, unknown[]> = {};
  const counts: Record<string, number> = {};
  for (const t of DEXIE_MIGRATION_TABLES) {
    const table = (db as unknown as Record<string, { toArray: () => Promise<unknown[]> }>)[t];
    if (!table) continue;
    const all = await table.toArray();
    const filtered = all.filter(
      (e: unknown) => (e as { tenant_id?: string }).tenant_id === tenantId || t === 'tenants',
    );
    data[t] = filtered;
    counts[t] = filtered.length;
  }
  // A-22 : inclure les données module (congés, saisie, conformité, etc.)
  const moduleRows = await SecureTenantStore.listForTenant(tenantId);
  data['module_data'] = moduleRows;
  counts['module_data'] = moduleRows.length;
  return { data, counts };
}

async function importToSqlite(data: Record<string, unknown[]>): Promise<Record<string, number>> {
  const restored: Record<string, number> = {};
  for (const table of DEXIE_MIGRATION_TABLES) {
    const rows = data[table];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      await SqliteEntityBridge.put(table, row as { id: string });
    }
    restored[table] = rows.length;
  }
  const moduleRows = data['module_data'];
  if (Array.isArray(moduleRows)) {
    for (const row of moduleRows) {
      await SqliteEntityBridge.put('module_data', row as { id: string });
    }
    restored['module_data'] = moduleRows.length;
  }
  return restored;
}

async function importToDexie(
  db: NexxusDatabase,
  data: Record<string, unknown[]>,
): Promise<Record<string, number>> {
  const restored: Record<string, number> = {};
  for (const [tableName, rows] of Object.entries(data)) {
    if (!DEXIE_MIGRATION_TABLES.includes(tableName as (typeof DEXIE_MIGRATION_TABLES)[number])) {
      continue;
    }
    const table = (
      db as unknown as Record<
        string,
        { bulkPut: (rows: unknown[]) => Promise<unknown> } | undefined
      >
    )[tableName];
    if (!table || !Array.isArray(rows)) continue;
    await table.bulkPut(rows);
    restored[tableName] = rows.length;
  }
  // A-22 : restaurer module_data même hors tables Dexie (miroir localStorage)
  const moduleRows = data['module_data'];
  if (Array.isArray(moduleRows)) {
    for (const raw of moduleRows) {
      const row = raw as ModuleDataRowLike;
      if (!row?.tenant_id || !row?.namespace) continue;
      await SecureTenantStore.setJson(row.tenant_id, row.namespace, row.value);
    }
    restored['module_data'] = moduleRows.length;
  }
  return restored;
}

interface ModuleDataRowLike {
  tenant_id?: string;
  namespace?: string;
  value?: unknown;
}

export function backupTableNames(): string[] {
  return [...DEXIE_MIGRATION_TABLES, ...SQLITE_AUX_TABLES.filter((t) => t === 'module_data')];
}
